import type React from "react";
import {
  Avatar,
  Box,
  Button,
  Divider,
  Popover,
  Typography,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import LogoutIcon from "@mui/icons-material/Logout";
import FmdGoodOutlinedIcon from "@mui/icons-material/FmdGoodOutlined";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { ProfilePopoverProps } from "./types";
import { logout } from "../../../redux/slices/auth.slice";
import { logoutUser } from "../../../common/authentication/AuthLogin";
import { useNavigate, useLocation } from "react-router-dom";
import { RootState } from "../../../redux/store/store";
import { useAwsControllerServiceGetFile } from "../../../services/queries";
import { isObject } from "@mui/x-data-grid/internals";
import { object } from "yup";

export const ProfilePopover: React.FC<ProfilePopoverProps> = ({
  anchorEl,
  onClose,
  onLocationClick,
  locations = [],
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const profile = useSelector((state: RootState) => state.auth.user);

  const open = Boolean(anchorEl);
  const avatarKey = profile?.avatar || "";
  const { data: avatarUrlData } = useAwsControllerServiceGetFile(
    { key: avatarKey },
    [],
    { enabled: !!avatarKey && !avatarKey.startsWith("http") },
  );
  const avatarSrc = avatarKey.startsWith("http")
    ? avatarKey
    : (avatarUrlData?.data as unknown as string) || undefined;
  const isEmployerRole =
    profile?.roleType === "EMPLOYER" || profile?.roleType === "AGENT";
  const isBrokerPortal = pathname.startsWith("/broker-portal");
  const isBrokerRole = profile?.role === "broker-admin";
  const isAffiliatePortal = profile?.roleType === "AFFILIATE";
  const isAffiliateGroup =
    isAffiliatePortal && profile?.affiliateGroupName !== null;
  const isAffiliateProvider = isAffiliatePortal && !isAffiliateGroup;
  const showLocationSwitcher =
    (!isEmployerRole && !isBrokerPortal && !isAffiliatePortal) ||
    isAffiliateProvider;
  const handleLogout = () => {
    dispatch(logout());
    localStorage?.setItem("isAuth", "false");
    navigate("/auth/login");
    logoutUser();
  };

  const addressString = isAffiliateGroup
    ? profile?.locationDetailResponseDto?.locationName
    : "";
  const affiliateGroupAddress = isAffiliatePortal
    ? [
        profile?.affiliateGroupAddress?.line1,
        profile?.affiliateGroupAddress?.line2,
        profile?.affiliateGroupAddress?.city,
        profile?.affiliateGroupAddress?.state,
        profile?.affiliateGroupAddress?.zipcode,
        profile?.affiliateGroupAddress?.country,
      ]
        .filter(Boolean)
        .join(", ")
    : "";

  const handleLocationClickInternal = (
    event: React.MouseEvent<HTMLElement>,
  ) => {
    event.stopPropagation();
    onLocationClick(event);
  };

  const getCurrentLocation = () => {
    const profileLocation = profile?.locationDetailResponseDto;
    if (profileLocation?.locationUuid) {
      return {
        name: profileLocation?.locationName,
        address: `${profileLocation?.address?.city}, ${profileLocation?.address?.state}`,
        isFromLastLoggedIn: profileLocation?.lastLoggedIn || false,
      };
    }

    const currentLocation = locations?.find((loc) => loc?.lastLoggedIn);
    if (currentLocation) {
      return {
        name: currentLocation?.locationName,
        address: `${currentLocation?.address?.city}, ${currentLocation?.address?.state}`,
        isFromLastLoggedIn: true,
      };
    }

    if (locations?.length > 0) {
      return {
        name: locations[0]?.locationName,
        address: `${locations[0]?.address?.city}, ${locations[0]?.address?.state}`,
        isFromLastLoggedIn: false,
      };
    }

    return {
      name: profile?.practiceName,
      address: profile?.location,
      isFromLastLoggedIn: false,
    };
  };

  const currentLocationData = getCurrentLocation();

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "right",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      slotProps={{
        paper: {
          sx: {
            width: 320,
            mt: 1,
            borderRadius: 2,
            boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
            border: "1px solid #E0E0E0",
          },
        },
      }}
    >
      <Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            p: 2,
            cursor: "pointer",
          }}
          onClick={() => navigate("/profile")}
        >
          <Avatar
            sx={{
              width: 48,
              height: 48,
              bgcolor: "#2F4853",
              fontSize: 18,
              fontWeight: "bold",
            }}
            alt={`${profile?.firstName}`}
            src={avatarSrc}
          >
            {profile?.firstName?.charAt(0) || "U"}
          </Avatar>
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                fontSize: 16,
                color: "#1A1A1A",
                mb: 0.5,
                cursor: "pointer",
              }}
            >
              {profile?.firstName || profile?.lastName
                ? `${profile?.firstName || ""} ${profile?.lastName || ""}`.trim()
                : "Unknown User"}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "#666666",
                fontSize: 14,
                cursor: "pointer",
              }}
            >
              {profile?.role || "Provider"}
            </Typography>
            {isAffiliatePortal && (
              <Typography
                variant="body2"
                sx={{
                  color: "#666666",
                  fontSize: 14,
                  cursor: "pointer",
                }}
              >
                {profile?.affiliateGroupName || ""}
              </Typography>
            )}
            {isBrokerRole && (
              <Typography
                variant="body2"
                sx={{
                  color: "#666666",
                  fontSize: 14,
                  cursor: "pointer",
                }}
              >
                {profile?.agentFirmName || ""}
              </Typography>
            )}
            {profile?.roleType === "EMPLOYER" && profile?.employerName && (
              <Typography
                variant="body2"
                sx={{
                  color: "#444444",
                  fontSize: 13,
                  cursor: "pointer",
                  mt: 0.25,
                }}
              >
                <Box
                  component="span"
                  sx={{ fontWeight: 600, color: "#666666" }}
                >
                  Employer:{" "}
                </Box>
                {profile.employerName}
              </Typography>
            )}
          </Box>
        </Box>
        {isAffiliateGroup && (
          <Box
            data-location-box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 1,
              px: 2,
              py: 1,

              borderRadius: 1,
              mx: 1,
            }}
          >
            <FmdGoodOutlinedIcon
              sx={{
                color: "#666666",
                fontSize: 18,
                mt: 0.3,
              }}
            />
            <Box sx={{ flex: 1 }}>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: "#1A1A1A",
                    fontSize: 14,
                    fontWeight: 400,
                  }}
                >
                  {addressString || affiliateGroupAddress}
                </Typography>
              </Box>
            </Box>
          </Box>
        )}
        {showLocationSwitcher && (
          <Box
            data-location-box
            onClick={handleLocationClickInternal}
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 1,
              px: 2,
              py: 1,
              cursor: "pointer",
              borderRadius: 1,
              mx: 1,
              "&:hover": {
                backgroundColor: "#F5F5F5",
              },
            }}
          >
            <FmdGoodOutlinedIcon
              sx={{
                color: "#666666",
                fontSize: 18,
                mt: 0.3,
              }}
            />
            <Box sx={{ flex: 1 }}>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: "#1A1A1A",
                    fontSize: 14,
                    fontWeight: 500,
                  }}
                >
                  {currentLocationData?.name}
                </Typography>
                {currentLocationData?.isFromLastLoggedIn && (
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: 10,
                      color: "#1976D2",
                      backgroundColor: "#E3F2FD",
                      px: 0.75,
                      py: 0.125,
                      borderRadius: 0.75,
                      fontWeight: 500,
                      lineHeight: 1,
                    }}
                  >
                    Current
                  </Typography>
                )}
              </Box>
              <Typography
                variant="body2"
                sx={{
                  color: "#666666",
                  fontSize: 13,
                }}
              >
                {currentLocationData?.address}
              </Typography>
            </Box>
            <ArrowDropDownIcon
              sx={{
                color: "#666666",
                fontSize: 18,
                mt: 0.3,
              }}
            />
          </Box>
        )}
        <Divider sx={{ my: 1 }} />

        <Button
          fullWidth
          onClick={handleLogout}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            justifyContent: "flex-start",
            textTransform: "none",
            color: "#D32F2F",
            fontSize: 14,
            py: 1,
            px: 2,
            "&:hover": {
              backgroundColor: "#FFEBEE",
            },
          }}
        >
          <LogoutIcon sx={{ fontSize: 18 }} />
          Log Out
        </Button>
      </Box>
    </Popover>
  );
};
